/* eslint-disable camelcase, no-else-return */
import * as gql from './gql';
import { UnreachableCaseError } from './types/utilities';
import { MembershipTierWithAnon } from './types/MembershipTierWithAnon';
import { PLAN_SKUS } from './gaia';

export type Locale = 'en' | 'es';
export type Phrase = (l: Locale) => string;
const localeToLanguageMap: { [K in Locale]: gql.typesClient.Language } = {
  en: gql.typesClient.Language.ENGLISH,
  es: gql.typesClient.Language.SPANISH,
};

const { Free, Digital, Premium } = gql.typesClient.MembershipTier;
const { YEAR } = gql.typesClient.MembershipPlanInterval;

export const getLanguageFromLocale = (locale: Locale): gql.typesClient.Language =>
  localeToLanguageMap[locale] || gql.typesClient.Language.ENGLISH;
export function getLocaleFromLanguage(language: gql.typesClient.Language): Locale {
  let locale: Locale = 'en';
  Object.entries(localeToLanguageMap).forEach(([loc, lang]) => {
    if (lang === language) locale = loc as Locale;
  });
  return locale;
}
export const getAllLocales = () => Object.keys(localeToLanguageMap);

export const isLocale = (l: string): l is Locale => Object.keys(localeToLanguageMap).includes(l);
export const isLanguage = (l: string): l is gql.typesClient.Language =>
  Object.values(localeToLanguageMap).includes(l as gql.typesClient.Language);

export function img(path: string, width: number) {
  // fallback to imgix:
  // return `https://yogainternational.imgix.net${path}?w=${width},q=80`;

  // Cloudflare Image Resizing:
  return `https://images-s3.yogainternational.com/cdn-cgi/image/width=${width},quality=70,format=auto${path}`;
}

type Course = gql.typesClient.Query_Course_Course | gql.typesSsr.Query_Course_yi_Course;
const { CourseAccess, MembershipTier } = gql.typesClient;

// Returns the reason the user would have access to the course (CourseAccessType).
// If returning undefined, the user does not have access.
// Does not account for expired memberships (see: `getUserCourseAccess`)
export function getCourseAccessType(
  user: gql.typesClient.Query_CurrentUser['CurrentUser'],
  course: Pick<Course, 'id' | 'permissions'>,
  accessTier?: MembershipTierWithAnon | null, // @TODO make required param after updating all clients
): gql.typesClient.CourseAccess | undefined {
  if (!user) return undefined;

  if (user.purchased_courses?.find(c => c && c.course.id === course.id)) {
    return CourseAccess.A_La_Carte;
  }

  const legacyTier = user.membership_tier; // @TODO use only `accessTier` after updating all clients
  const tier = accessTier || legacyTier;

  if (tier === 'Anon') return undefined;
  if (course.permissions.includes(tier)) {
    if (tier === MembershipTier.Free) return CourseAccess.Free;
    if (tier === MembershipTier.Digital) return CourseAccess.Digital;
    if (tier === MembershipTier.Premium) return CourseAccess.Premium;
  }
  return undefined;
}

/*
 * * * * * * * * DEPRECATED (grants access even to users with expired membership)
 *                   Please use `getUserCourseAccess` instead
 */
export const hasAccess = (...args: Parameters<typeof getCourseAccessType>) =>
  !!getCourseAccessType(...args);

type Permission =
  | { hasAccess: true; reason: gql.typesClient.CourseAccess }
  | { hasAccess: false; issue: 'expired_membership' | 'no_account' | 'no_access' };
export const getUserCourseAccess = (
  user: gql.typesClient.Query_CurrentUser['CurrentUser'],
  course: Pick<Course, 'id' | 'permissions'>,
  accessTier?: MembershipTierWithAnon | null, // @TODO make required param after updating all clients
): Permission => {
  if (!user) return { hasAccess: false, issue: 'no_account' };

  // Handle legacy behavior, when `accessTier` was not supplied, and Brooklyn JWT not considered
  // @TODO remove when `accessTier` is made a required param, and all clients are updated
  if (!accessTier) {
    const accessType = getCourseAccessType(user, course);
    if (!accessType) return { hasAccess: false, issue: 'no_access' };
    if (accessType === CourseAccess.Digital || accessType === CourseAccess.Premium) {
      if (!user.membership_is_valid) return { hasAccess: false, issue: 'expired_membership' };
    }
    return { hasAccess: true, reason: accessType };
  }

  // Handle all access sources, including `accessTier` provided by Brooklyn user JWT.
  const accessType = getCourseAccessType(user, course, accessTier);
  if (!accessType) return { hasAccess: false, issue: 'no_access' };
  return { hasAccess: true, reason: accessType };
};

const liveEmbedShape = {
  teacherName: '',
  startTime: '',
  endTime: '',
  channelId: '',
  playlistId: '',
  meetingLink: '',
};
type LiveEmbed = typeof liveEmbedShape;
export const parseLiveModuleJSON = (moduleJSON: string): LiveEmbed | Error => {
  try {
    const liveEmbed = JSON.parse(moduleJSON);
    Object.entries(liveEmbedShape).forEach(([k, v]) => {
      const type = typeof liveEmbed[k];
      const typeofV = typeof v;
      if (type !== typeofV) throw new Error(`Invalid field: ${k} ${type} ${v}`);
    });
    return liveEmbed;
  } catch (e) {
    return e as Error;
  }
};

export function hasMonthlyMembership(
  CurrentUserSubscription: gql.typesClient.Query_CurrentUserSubscription_CurrentUserSubscription,
) {
  return (
    CurrentUserSubscription &&
    !CurrentUserSubscription.product_name.includes('Annual') &&
    CurrentUserSubscription.gateway_shortname === 'Stripe'
  );
}

type CardCourse = gql.typesSsr.Query_Course_CoursePlayerPage_yi_Course;
export function getCourseType(course: Pick<CardCourse, 'categories'> | undefined | null) {
  if (!course?.categories) return 'course';
  if (course.categories.some(category => category?.name === 'Community')) {
    return 'community';
  }
  if (course.categories.some(category => category?.name === 'Podcast')) {
    return 'podcast';
  }
  return 'course';
}

export function timeCodeFromSeconds(totalSeconds: number | string | false) {
  if (totalSeconds === false) return '0:00';
  let seconds = parseInt(totalSeconds.toString(), 10);
  if (Number.isNaN(seconds)) return seconds;

  let result = '';

  let minutes = Math.floor(seconds / 60);
  seconds %= 60;
  const hours = Math.floor(minutes / 60);
  minutes %= 60;

  if (hours) result += `${hours}:`;

  if (minutes < 10) result += 0;
  result += `${minutes}:`;

  if (seconds < 10) result += 0;
  result += seconds;

  return result;
}

export function formatTime(time: number, currentFormat: 'seconds' | 'minutes') {
  return {
    hours: currentFormat === 'seconds' ? Math.floor(time / 60 / 60) : Math.floor(time / 60),
    minutes: currentFormat === 'seconds' ? Math.floor((time / 60) % 60) : time % 60,
    seconds: currentFormat === 'seconds' ? time % 60 : 0,
  };
}

type Tabs = 'outline' | 'overview' | 'comments';
type CourseTypes = 'course' | 'podcast' | 'community';
export function getTabDisplayName(tabName: Tabs, courseType: CourseTypes) {
  const names = {
    outline: {
      course: 'outline',
      community: 'community_classes',
      podcast: 'episodes',
    },
    overview: {
      course: 'overview',
      community: 'about',
      podcast: 'about',
    },
    comments: {
      course: 'comments',
      community: 'discussion',
      podcast: 'comments',
    },
  };
  return names[tabName]?.[courseType] ? names[tabName][courseType] : tabName;
}

// @TODO satisfy these MTL needs on GraphQL server
type MTLCourseMetaData = { id: string; img: string };
const MTLCourses: { [k in Locale | 'ltl']: MTLCourseMetaData } = {
  en: { id: '4335974', img: '/assets/content/site/mtl-register.png' },
  es: { id: '5551281', img: '/assets/content/site/en-vivo-register.jpg' },
  ltl: { id: '5658514', img: '/assets/content/site/ltl-register.png' },
};

export function getMTLCourseMeta(arg: Pick<Course, 'id'> | Locale) {
  if (typeof arg === 'string') return MTLCourses[arg] || null;
  return Object.values(MTLCourses).find(({ id }) => arg.id === id);
}

export const isMTLCourse = (course: Pick<Course, 'id'>) => !!getMTLCourseMeta(course);

// @TODO update imports on gatsby for these functions, and remove from gatsby utilities

function getDaysFromNow(date: Date) {
  const miliSecondInOneDay = 24 * 60 * 60 * 1000;
  const givenDate = new Date(date);
  const dateToday = new Date();
  const diff = Math.round((dateToday.getTime() - givenDate.getTime()) / miliSecondInOneDay);
  return diff;
}
export function getDaysUntil(date: Date) {
  const diff = getDaysFromNow(date);
  return diff < 0 ? Math.abs(diff) : 0;
}
export function getDaysSince(date: Date) {
  const diff = getDaysFromNow(date);
  return diff < 0 ? 0 : diff;
}

export type TimeOfDayName = 'morning' | 'afternoon' | 'evening' | 'bedtime';
export function getTimeOfDayName(): TimeOfDayName {
  const date = new Date();
  const hours = date.getHours();
  if (hours >= 2 && hours < 12) return 'morning'; // 2a - 11:59a
  if (hours >= 12 && hours < 17) return 'afternoon'; // 12p - 4:59p
  if (hours >= 17 && hours < 21) return 'evening'; // 5p - 9p
  return 'bedtime'; // 9p - 1:59a
}

type TimeOfDayId = '196' | '197' | '198' | '283';
const timeOfDayIds: { [k in TimeOfDayName]: TimeOfDayId } = {
  morning: '196',
  evening: '197',
  bedtime: '198',
  afternoon: '283',
};
export function getTimeOfDayCategoryId(timeOfDayName?: TimeOfDayName): TimeOfDayId {
  const timeOfDay = timeOfDayName || getTimeOfDayName();
  return timeOfDayIds[timeOfDay];
}

export const getMembershipTierDisplayName = (
  membership?: gql.typesClient.MembershipTier | 'Annual',
) => {
  if (!membership) return '';
  if (membership === 'Digital') return 'Basic';
  return membership;
};

const planIntervalDisplayNames: {
  [K in gql.typesClient.MembershipPlanInterval]: { en: string; es: string };
} = {
  MONTH: { en: 'Monthly', es: 'Mensualmente' },
  YEAR: { en: 'Annual', es: 'Anual' },
};
export const getPlanIntervalDisplayName = (
  interval: gql.typesClient.MembershipPlanInterval | null,
  locale: Locale,
) => (interval && planIntervalDisplayNames[interval][locale]) || '';

const getTrialText = (locale: Locale) => (locale === 'en' ? 'Trial' : 'Prueba gratis');

type Plan = Pick<
  gql.typesClient.Query_CurrentUserSubscription_CurrentUserSubscription,
  'tier' | 'plan_interval' | 'is_trial'
>;

interface MembershipPlanProps {
  plan: Plan & { sku?: string };
  locale: Locale;
}
export const getMembershipTierDisplayNameFull = ({
  plan: { tier, plan_interval, is_trial, sku },
  locale,
}: MembershipPlanProps) => {
  if (!tier) return '';
  if (sku === PLAN_SKUS.BASIC_3_MONTH_PROMO) {
    return locale === 'en' ? '3-Month Special Rate' : 'Tarifa especial de 3 meses';
  } else if (tier === Free) {
    return locale === 'en' ? 'Free Account (No Membership)' : 'Cuenta gratuita (Sin membresía)';
  } else if (tier === Digital && plan_interval === YEAR) {
    const descrip = is_trial
      ? getTrialText(locale)
      : getPlanIntervalDisplayName(plan_interval, locale);
    return `${locale === 'en' ? 'Annual Membership' : 'Membresía anual'} (${descrip})`;
  } else if (tier === Digital) {
    const descrip = is_trial
      ? getTrialText(locale)
      : getPlanIntervalDisplayName(plan_interval, locale);
    return `${locale === 'en' ? 'Monthly Membership' : 'Membresía mensual'} (${descrip})`;
  } else if (tier === Premium) {
    const descrip = is_trial
      ? getTrialText(locale)
      : getPlanIntervalDisplayName(plan_interval, locale);
    return `${locale === 'en' ? 'Premium Membership' : 'Membresía premium'} (${descrip})`;
  } else {
    console.warn(new UnreachableCaseError(tier));
    return `${tier} (${plan_interval})`;
  }
};

interface MembershipPlanRNProps {
  membership: gql.typesClient.MembershipTier;
  subscription: gql.typesClient.MembershipPlanInterval | null;
  locale: Locale;
}
export const getMembershipTierDisplayNameFullRN = ({
  membership,
  subscription,
  locale,
}: MembershipPlanRNProps) => {
  if (!membership) return '';
  if (membership === Free) {
    return locale === 'en' ? 'Free Account (No Membership)' : 'Cuenta gratuita (Sin membresía)';
  } else if (membership === Digital) {
    const descrip = getPlanIntervalDisplayName(subscription, locale);
    return locale === 'en' ? `${descrip} Membership` : `Membresía ${descrip}`;
  } else if (membership === Premium) {
    const descrip = getPlanIntervalDisplayName(subscription, locale);
    return `${locale === 'en' ? 'Premium Membership' : 'Membresía premium'} (${descrip})`;
  } else {
    return `${membership} (${subscription})`;
  }
};

interface LabelReplacementProps {
  label: string;
  locale?: Locale;
}
export const getLabelReplacement = ({ label, locale = 'en' }: LabelReplacementProps) => {
  if (!label) return '';
  if (label.toLowerCase() === 'free') return locale === 'en' ? 'Free' : 'Gratuita';
  if (label.toLowerCase() === 'digital') return locale === 'en' ? 'Basic' : 'Básica';
  return label;
};

export const isYIPlaylist = ({ playlistUserId }: { playlistUserId: string }) => {
  const YIPlaylistsUserIds = ['136'];
  return YIPlaylistsUserIds.includes(playlistUserId);
};

export const getPrivacyPolicyUrl = (locale: Locale): string => {
  switch (locale) {
    case 'es':
      return 'https://try.yogainternational.com/brochure-politica-de-privacidad';
    default:
      return 'https://try.yogainternational.com/brochure-privacy-policy';
  }
};
